/**
 * Local ranking for the count search sheet.
 *
 * The reported failure was twofold: fragments in the wrong order ("kiste obst") found nothing,
 * because the server matched the query as one contiguous run, and results arrived in SKU order,
 * so an article that did match could sit below the visible cut. Both are fixed here rather than
 * in the API: the whole assortment is already on the device (one fetch per session), so matching
 * and ranking cost no round trip and keep working in a dead spot.
 *
 * Matching is token-AND: every whitespace-separated token must appear somewhere in the article's
 * name or SKU, in any order. Ranking is exact-SKU, then name-prefix, then word-prefix, then plain
 * substring — with an article that is not yet counted lifted half a rank, because during a count
 * the wanted article is almost always still outstanding. Ties break on name in German collation.
 *
 * The assortment payload carries no separate display name (see `CountAssortmentItem`), so `name`
 * and `sku` are the whole haystack.
 */

export interface SearchableAssortmentItem {
  sku: string
  name: string
}

const RANK_EXACT_SKU = 0
const RANK_NAME_PREFIX = 1
const RANK_WORD_PREFIX = 2
const RANK_SUBSTRING = 3
/** Lift for a not-yet-counted article: enough to win its own rank, never to jump a better one. */
const OPEN_BOOST = 0.5

function byName(a: SearchableAssortmentItem, b: SearchableAssortmentItem) {
  return a.name.localeCompare(b.name, 'de')
}

/**
 * Ranks `items` for `query`, best first. An empty query lists the whole assortment with the
 * outstanding articles first — which is what the counter wants to see when the sheet opens.
 * A query that matches nothing returns an empty array; the sheet then offers the wider catalogue.
 */
export function rankAssortment<T extends SearchableAssortmentItem>(
  items: ReadonlyArray<T>,
  countedSkus: ReadonlySet<string>,
  query: string,
): Array<T> {
  const raw = query.trim().toLowerCase()
  const counted = (item: T) => countedSkus.has(item.sku)

  if (raw === '') {
    return [
      ...items.filter((item) => !counted(item)).sort(byName),
      ...items.filter(counted).sort(byName),
    ]
  }

  const tokens = raw.split(/\s+/)
  const scored: Array<{ item: T; rank: number }> = []

  for (const item of items) {
    // One haystack for name and SKU: a token can never contain whitespace, so a match can never
    // span the joining space — this is exactly "substring of the name, or of the SKU".
    const haystack = `${item.name} ${item.sku}`.toLowerCase()
    if (!tokens.every((token) => haystack.includes(token))) continue

    const name = item.name.toLowerCase()
    let rank = RANK_SUBSTRING
    if (item.sku.toLowerCase() === raw) {
      rank = RANK_EXACT_SKU
    } else if (name.startsWith(raw)) {
      rank = RANK_NAME_PREFIX
    } else if (
      name
        .split(/[\s-]+/)
        .some((word) => tokens.some((token) => word.startsWith(token)))
    ) {
      rank = RANK_WORD_PREFIX
    }
    // An exact SKU hit is already unambiguous and stays first whether it was counted or not.
    if (rank > RANK_EXACT_SKU && !counted(item)) rank -= OPEN_BOOST

    scored.push({ item, rank })
  }

  scored.sort((a, b) => a.rank - b.rank || byName(a.item, b.item))
  return scored.map((entry) => entry.item)
}
